import { useEffect } from "react";
import "@/App.css";
import Lenis from "lenis";
import { useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { EventDetails } from "@/components/EventDetails";
import { Fundraising } from "@/components/Fundraising";
import { Sponsors } from "@/components/Sponsors";
import { Committee } from "@/components/Committee";
import { About } from "@/components/About";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { PAGES, pathToKey } from "@/seo/pages";

// Drives per-route <head> meta and scrolls to the matching section on load.
// The single-page shell below stays mounted, so animations/state are preserved.
function RouteMeta() {
  const location = useLocation();
  const page = PAGES[pathToKey(location.pathname)] || PAGES.home;

  useEffect(() => {
    let cancelled = false;
    let interacted = false;
    const onInteract = () => { interacted = true; };
    window.addEventListener("wheel", onInteract, { passive: true });
    window.addEventListener("touchstart", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);

    const target = page.section;
    const doScroll = () => {
      if (cancelled || interacted) return;
      const lenis = window.__lenis;
      if (target === "top") {
        if (lenis) lenis.scrollTo(0);
        else window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const el = document.getElementById(target);
      if (!el) return;
      if (lenis) lenis.scrollTo(el, { offset: -80 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    // Re-align a few times: async content (e.g. the live Champions roster) can grow
    // the page height after the first scroll, so we re-run until layout settles —
    // unless the visitor has already started scrolling themselves.
    const timers = [300, 1200, 2600].map((ms) => setTimeout(doScroll, ms));

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.removeEventListener("wheel", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return <Seo page={page} />;
}

function App() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.15, lerp: 0.09, smoothWheel: true, anchors: { offset: -80 } });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App grain">
      <RouteMeta />
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <EventDetails />
        <Fundraising />
        <Sponsors />
        <Committee />
        <About />
      </main>
      <Footer />
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
}

export default App;
